<template>
  <div class="PokemonSingle">
    <div class="Info">
      <div class="ProductName">{{ data.product_name }}</div>
      <div class="CardInfo">{{ cardInfoString }}</div>
    </div>

    <TextLink
      :to="packPageLink"
      :text="data.pm_card.pack?.pack_name || ''"
      class="PackLink"
    />

    <div class="SellingNumLimitAnnounce" v-if="hasSellingNumLimit">
      <span v-if="data.selling_num_limit === 0">
        この商品は現在購入することができません
      </span>
      <span v-else>
        この商品は合計{{ data.selling_num_limit }}点まで購入いただけます
      </span>
    </div>

    <div class="Body">
      <div>
        <PMCardImage
          :alt="data.product_name"
          :url="data.pm_card.card_image1_url"
          :view-transition-name="disableViewTransition ? undefined : `thumbnail-${data.inventory_product_id}`"
          can-expand
        />

        <div v-if="!isInventoryEmpty">
          <div class="AboutCondition">
            <Help />
            <TextLink
              text="カードの状態について"
              target="_blank"
              to="/pokemon/conditions"
            />
          </div>
        </div>
      </div>

      <div class="Inventories">
        <template v-if="isInventoryEmpty">
          <div class="EmptyText">在庫切れです</div>
        </template>
        <template v-else>
          <InventorySelect
            v-if="data.rank_s_inventory.inventory_num"
            v-model="fields.rankSInventoryNum"
            rank="s"
            :inventory="data.rank_s_inventory"
            :num-in-cart="currentNumInCart.s"
            :has-error="enableCheck && isInventoryNumEmpty"
          />
          <InventorySelect
            v-if="data.rank_a_inventory.inventory_num"
            v-model="fields.rankAInventoryNum"
            rank="a"
            :inventory="data.rank_a_inventory"
            :num-in-cart="currentNumInCart.a"
            :has-error="enableCheck && isInventoryNumEmpty"
          />
          <InventorySelect
            v-if="data.rank_b_inventory.inventory_num"
            v-model="fields.rankBInventoryNum"
            rank="b"
            :inventory="data.rank_b_inventory"
            :num-in-cart="currentNumInCart.b"
            :has-error="enableCheck && isInventoryNumEmpty"
          />
          <InventorySelect
            v-if="data.rank_c_inventory.inventory_num"
            v-model="fields.rankCInventoryNum"
            rank="c"
            :inventory="data.rank_c_inventory"
            :num-in-cart="currentNumInCart.c"
            :has-error="enableCheck && isInventoryNumEmpty"
          />
        </template>
        <ErrorText
          v-if="enableCheck && isInventoryNumEmpty"
          text="数量を選択してください"
        />
      </div>
    </div>

    <div class="Actions">
      <div class="AddToCartWrapper">
        <ErrorText v-if="isReachingSellingNumLimit" :text="data.selling_num_limit === 0 ? 'この商品は現在購入することができません' : `カート内と合わせて${data.selling_num_limit}点を超えているため追加できません`"/>
        <Button
          text="カートに追加"
          v-if="!isInventoryEmpty"
          width="100%"
          @click="addToCart"
        >
          <template v-slot:icon>
            <Add />
          </template>
        </Button>
      </div>
    </div>

    <!-- 時刻はお客様の端末で計算するためSSRに含めない -->
    <div class="deliveryInfo">
      <ClientOnly>
        <DeliveryLimit
          :delivery-limit-date-label="deliveryLimitDateLabel"
          :delivery-limit-time-label="deliveryLimitTimeLabel"
        />
        <div class="deliveryFee">注文金額 4,000円以上で送料0円（当社負担）</div>
      </ClientOnly>
    </div>

    <div class="SellingDescription" v-if="!!data.selling_description?.trim()">
      <h2>特筆事項</h2>
      <div class="SellingDescriptionText" v-html="data.selling_description"></div>
    </div>

    <div class="AboutReturning" v-if="!isInventoryEmpty">
      <Help />
      <TextLink text="返品・交換について" target="_blank" to="/guides#returns" />
    </div>

    <div class="SameProducts" v-if="samePokemonSingleProducts.length">
      <h2>同名カードをさがす</h2>
      <SummaryList
        :items="samePokemonSingleProducts"
        :show-read-more="samePokemonSingleProducts.length < samePokemonSingleProductsTotal"
        @read-more="$emit('readMoreSamePokemonSingleProducts')"
        :disable-view-transition
      />
    </div>

    <div class="PreEvolutionProducts" v-if="preEvolutionPokemonSingleProducts.length">
      <h2>進化前のカードをさがす</h2>
      <SummaryList
        :items="preEvolutionPokemonSingleProducts"
        :show-read-more="preEvolutionPokemonSingleProducts.length < preEvolutionPokemonSingleProductsTotal"
        @read-more="$emit('readMorePreEvolutionPokemonSingleProducts')"
        :disable-view-transition
      />
    </div>

    <div class="EvolutionProducts" v-if="evolutionPokemonSingleProducts.length">
      <h2>進化後のカードをさがす</h2>
      <SummaryList
        :items="evolutionPokemonSingleProducts"
        :show-read-more="evolutionPokemonSingleProducts.length < evolutionPokemonSingleProductsTotal"
        @read-more="$emit('readMoreEvolutionPokemonSingleProducts')"
        :disable-view-transition
      />
    </div>

    <div class="PickupSections">
      <div class="PickupSection">
        <h2>汎用カード</h2>
        <SummaryList
          mode="horizontal"
          show-read-more
          read-more-link="/pokemon/pickups/general-purpose"
          read-more-label="汎用カード一覧"
          :items="pokemonSinglePickupGeneralPurposeProducts"
          item-class-name="from-pickup-section-for-generalPurpose"
          />
      </div>

      <div class="PickupSection">
        <h2>基本エネルギー</h2>
        <SummaryList
          mode="horizontal"
          show-read-more
          read-more-link="/pokemon/pickups/basic-energies"
          read-more-label="基本エネルギー一覧"
          :items="pokemonSinglePickupBasicEnergyProducts"
          item-class-name="from-pickup-section-for-basicEnergies"
          />
      </div>
    </div>

    <div class="Regulations">
      <h2>対応レギュレーション</h2>
      <div class="RegulationsText">
        <span
          v-for="regulation in data.pm_card.regulations"
          :key="regulation"
          >{{ regulation }}</span
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineNuxtComponent, useNuxtApp } from "#app";
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { DITokens } from "@/di";
import { TextLink, Button, ErrorText, PMCardImage } from "@ka-nabellinc/ka-nabell-ui";
// @ts-expect-error
import { Help, Add } from "@ka-nabellinc/ka-nabell-icons";
import type { ESPokemonSingleProduct } from "@ka-nabellinc/tcg-platform-es-schema";
import { useGTM } from '@/composables/useGTM'
import InventorySelect from "@/components/shared/Single/InventorySelect";
import SummaryList from '@/components/Pokemon/Single/SummarList'
import DeliveryLimit from "@/components/Order/DeliveryLimit";
import { getNumInCart, getDefaultFields, getCardInfoString } from "./helpers";
import type { NumInCart } from "./types";

export default defineNuxtComponent({
  components: {
    TextLink,
    Help,
    Add,
    Button,
    InventorySelect,
    ErrorText,
    PMCardImage,
    SummaryList,
    DeliveryLimit
  },
  props: {
    variantId: {
      type: Number
    },
    data: {
      type: Object as () => ESPokemonSingleProduct,
      required: true,
    },
    samePokemonSingleProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    },
    samePokemonSingleProductsTotal: {
      type: Number,
      default: 0
    },
    preEvolutionPokemonSingleProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    },
    preEvolutionPokemonSingleProductsTotal: {
      type: Number,
      default: 0
    },
    evolutionPokemonSingleProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    },
    evolutionPokemonSingleProductsTotal: {
      type: Number,
      default: 0
    },
    pokemonSinglePickupGeneralPurposeProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    },
    pokemonSinglePickupBasicEnergyProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    }
  },
  emits: [
    'readMoreSamePokemonSingleProducts',
    'readMorePreEvolutionPokemonSingleProducts',
    'readMoreEvolutionPokemonSingleProducts'
  ],
  setup(props) {
    const { $container } = useNuxtApp();
    const cartStore = $container.resolve(DITokens.CartStore);
    const cartState = storeToRefs(cartStore);
    const disableViewTransition = computed(() => cartState.showCart.value) // カート表示中はview transitionをoff
    const toastStore = $container.resolve(DITokens.ToastStore);
    const orderStore = $container.resolve(DITokens.OrderStore)
    const { deliveryLimitTimeLabel, deliveryLimitDateLabel } = storeToRefs(orderStore)
    const fields = ref(getDefaultFields());
    const enableCheck = ref(false);
    const { sendGTMEvent } = useGTM($container)
    const currentNumInCart = computed<NumInCart>(() =>
      getNumInCart(
        cartState.pokemonCart.value.items,
        props.data.inventory_product_id
      )
    );
    const cardInfoString = computed(() =>
      getCardInfoString(props.data.pm_card)
    );
    const packPageLink = computed(
      () =>
        `/pokemon/search?packId=${props.data.pm_card.pack?.pack_id}`
    );

    // 購入上限存在確認
    const hasSellingNumLimit = computed(
      () => typeof props.data.selling_num_limit === "number"
    );

    // 購入上限到達確認
    const isReachingSellingNumLimit = computed(() => {
      if (typeof props.data.selling_num_limit !== 'number') return false;

      const total =
        currentNumInCart.value.total +
        fields.value.rankSInventoryNum +
        fields.value.rankAInventoryNum +
        fields.value.rankBInventoryNum +
        fields.value.rankCInventoryNum;

      return total > props.data.selling_num_limit
    });

    const isInventoryEmpty = computed(() => {
      const totalShopInventoryNum =
        props.data.rank_s_inventory.inventory_num +
        props.data.rank_a_inventory.inventory_num +
        props.data.rank_b_inventory.inventory_num +
        props.data.rank_c_inventory.inventory_num;
      return !totalShopInventoryNum;
    });
    const isInventoryNumEmpty = computed(() => {
      const totalInventoryNum =
        fields.value.rankSInventoryNum +
        fields.value.rankAInventoryNum +
        fields.value.rankBInventoryNum +
        fields.value.rankCInventoryNum;
      return !totalInventoryNum;
    });

    // カート
    const addToCart = async () => {
      try {
        const {
          rankSInventoryNum,
          rankAInventoryNum,
          rankBInventoryNum,
          rankCInventoryNum,
        } = fields.value;
        const {
          product_name,
          rank_s_inventory,
          rank_a_inventory,
          rank_b_inventory,
          rank_c_inventory,
        } = props.data

        // 購入上限到達エラー
        if (isReachingSellingNumLimit.value) return

        enableCheck.value = true;

        // 1枚も追加カードが無い場合はエラー
        if (isInventoryNumEmpty.value) return;

        // 各状態の在庫数上限エラー
        const isRanksNumLimit =
          currentNumInCart.value.s + rankSInventoryNum >
          rank_s_inventory.inventory_num;
        const isRankANumLimit =
          currentNumInCart.value.a + rankAInventoryNum >
          rank_a_inventory.inventory_num;
        const isRankBNumLimit =
          currentNumInCart.value.b + rankBInventoryNum >
          rank_b_inventory.inventory_num;
        const isRankCNumlimit =
          currentNumInCart.value.c + rankCInventoryNum >
          rank_c_inventory.inventory_num;
        if (
          isRanksNumLimit ||
          isRankANumLimit ||
          isRankBNumLimit ||
          isRankCNumlimit
        )
          return;

        cartStore.addSingleProductToPokemonCart({
          product: props.data,
          s: rankSInventoryNum,
          a: rankAInventoryNum,
          b: rankBInventoryNum,
          c: rankCInventoryNum,
        });
        toastStore.setToast({
          mode: "success",
          message: "カートに追加しました",
        });

        // GTM Event
        let items = [] as GTMSingleItemType[]

        if (rankSInventoryNum) {
          items.push({
            item_id: rank_s_inventory.inventory_id,
            item_name: product_name,
            item_category: 'ポケモンカード',
            price: rank_s_inventory.selling_price,
            quantity: rankSInventoryNum
          })
        }

        if (rankAInventoryNum) {
          items.push({
            item_id: rank_a_inventory.inventory_id,
            item_name: product_name,
            item_category: 'ポケモンカード',
            price: rank_a_inventory.selling_price,
            quantity: rankAInventoryNum
          })
        }

        if (rankBInventoryNum) {
          items.push({
            item_id: rank_b_inventory.inventory_id,
            item_name: product_name,
            item_category: 'ポケモンカード',
            price: rank_b_inventory.selling_price,
            quantity: rankBInventoryNum
          })
        }

        if (rankCInventoryNum) {
          items.push({
            item_id: rank_c_inventory.inventory_id,
            item_name: product_name,
            item_category: 'ポケモンカード',
            price: rank_c_inventory.selling_price,
            quantity: rankCInventoryNum
          })
        }

        sendGTMEvent({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'JPY',
            value: items.reduce((acc, ac) => acc + (ac.price * ac.quantity), 0),
            items
          }
        })

        // 初期化
        enableCheck.value = false;
        fields.value = getDefaultFields();
      } catch (e) {
        console.log("add to Cart error");
        console.log(e);
      }
    };

    return {
      fields,
      enableCheck,
      cardInfoString,
      packPageLink,
      currentNumInCart,

      hasSellingNumLimit,
      isReachingSellingNumLimit,
      isInventoryEmpty,
      isInventoryNumEmpty,
      addToCart,

      deliveryLimitTimeLabel,
      deliveryLimitDateLabel,
      disableViewTransition,
    };
  },
});
</script>

<style scoped>
.PokemonSingle {
  width: 100%;
}

.Info {
  width: 100%;
  margin-bottom: 8px;
  border-bottom-style: solid;
  border-bottom-width: 3px;
  border-image-source: linear-gradient(
    45deg,
    #ffa6dc 0%,
    #ffcc84 50%,
    #ffed4a 100%
  );
  border-image-slice: 1;
  padding-bottom: 9px;
}

.ProductName {
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.36px;
}

.CardInfo {
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.PackLink {
  margin-bottom: 20px;
  width: 100%;
  word-break: break-all;
}

.SellingNumLimitAnnounce {
  margin-bottom: 20px;
  width: 100%;
  padding: 4px 8px;
  text-align: center;
  background-color: #fff2f5;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: 0.12px;
  color: #f24141;
}

.Body {
  width: 100%;
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.Body > * {
  width: calc((100% - 16px) / 2);
}

.MainImage {
  width: 100%;
}

.AboutCondition {
  display: flex;
  margin-top: 8px;
}

.Inventories {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.EmptyText {
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.Actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 16px;
}

.AddToCartWrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.Regulations {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.RegulationsText {
  display: flex;
  gap: 16px;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.SellingDescription {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}

.SellingDescriptionText {
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.14px;
}

.AboutReturning {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 32px;
}

.SameProducts,
.PreEvolutionProducts,
.EvolutionProducts {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.deliveryInfo {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
  min-height: 38px; /* Layout Shiftの対策 */
}

.deliveryFee {
  font-size: 12px;
  line-height: 150%;
  font-weight: 700;
  letter-spacing: 0.15px;
}

:deep(.SellingDescriptionText a) {
  text-decoration: underline;
  text-decoration-style: solid;
  text-decoration-thickness: 0.8px;
  text-underline-offset: 3px;
}

.PickupSections {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
}

.PickupSection {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
